/**
 * AdminExpiredWarrantsButton — Round P154 (Jun 2026).
 *
 * Cleanup triage for warrants that expired 30+ days ago and still sit
 * in the database (e.g. RNWWW after Aug 2026). Per row: ticker opens
 * the Stock Modal, CIK links to the issuer's EDGAR filings, trash
 * (2-click confirm) blocklists + purges + deletes the warrant row, and
 * the X drops it from this list only (wrong-expiration error case).
 */
import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, X, Trash2, ExternalLink, Hourglass } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminExpiredWarrantsButton({ onStockClick }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [armedTicker, setArmedTicker] = useState(null);
  const [busyTicker, setBusyTicker] = useState(null);

  const openModal = async () => {
    setOpen(true);
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/expired-warrants`, { withCredentials: true });
      setRows(data.rows || []);
    } catch (err) {
      toast.error('Could not load expired warrants', { description: err?.response?.data?.detail || err?.message });
    } finally {
      setLoading(false);
    }
  };

  const dropRow = (ticker) => setRows((prev) => prev.filter((r) => r.ticker !== ticker));

  const trash = async (ticker) => {
    if (busyTicker) return;
    if (armedTicker !== ticker) {
      setArmedTicker(ticker);
      setTimeout(() => setArmedTicker((cur) => (cur === ticker ? null : cur)), 5000);
      return;
    }
    setBusyTicker(ticker);
    try {
      await axios.post(`${API}/admin/expired-warrants/${ticker}/trash`, {}, { withCredentials: true });
      toast.success(`${ticker} blocklisted`, { description: 'Purged from all caches and removed from the warrant tracker.' });
      dropRow(ticker);
      setArmedTicker(null);
    } catch (err) {
      toast.error(`Trash failed for ${ticker}`, { description: err?.response?.data?.detail || err?.message });
    } finally {
      setBusyTicker(null);
    }
  };

  const dismiss = async (ticker) => {
    if (busyTicker) return;
    setBusyTicker(ticker);
    try {
      await axios.post(`${API}/admin/expired-warrants/${ticker}/dismiss`, {}, { withCredentials: true });
      toast.info(`${ticker} dropped from the cleanup list`, { description: 'Still tracked as a warrant — nothing was deleted.' });
      dropRow(ticker);
    } catch (err) {
      toast.error(`Dismiss failed for ${ticker}`, { description: err?.response?.data?.detail || err?.message });
    } finally {
      setBusyTicker(null);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold bg-zinc-500/10 text-zinc-600 dark:text-zinc-300 border border-zinc-500/30 hover:bg-zinc-500/20 transition-colors"
        data-testid="admin-expired-warrants-btn"
      >
        <Hourglass className="w-3.5 h-3.5" />
        Expired Warrants
      </button>
      {open && (
        <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/60 p-4" onClick={() => setOpen(false)}>
          <div
            className="w-full max-w-3xl max-h-[85vh] overflow-y-auto rounded-lg border border-border bg-card shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="admin-expired-warrants-modal"
          >
            <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-3 bg-card border-b border-border">
              <div>
                <h3 className="text-sm font-bold flex items-center gap-2">
                  <Hourglass className="w-4 h-4 text-zinc-500" /> Expired Warrants — 30+ days past expiration ({rows.length})
                </h3>
                <p className="text-[10.5px] text-muted-foreground mt-0.5">
                  Click a <b>ticker</b> to inspect in the Stock Modal. <b>EDGAR</b> opens the issuer&apos;s SEC filings. <b>Trash</b> (two clicks) blocklists + purges. <b>X</b> just drops it from this list (wrong-date error case) — nothing is deleted.
                </p>
              </div>
              <button type="button" onClick={() => setOpen(false)} className="p-1 rounded hover:bg-muted" data-testid="expired-warrants-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            {loading ? (
              <div className="flex items-center justify-center py-10 text-muted-foreground text-xs">
                <Loader2 className="w-4 h-4 animate-spin mr-2" /> Scanning warrants…
              </div>
            ) : rows.length === 0 ? (
              <div className="px-4 py-8 text-center text-xs text-muted-foreground">No expired warrants awaiting cleanup. 🎉</div>
            ) : (
              <div>
                <div className="grid grid-cols-[72px_1fr_92px_70px_64px_56px_32px_32px] gap-2 px-3 py-2 bg-muted/40 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                  <div>Ticker</div><div>Name</div><div>Expired</div><div>Days ago</div><div>Price</div><div>EDGAR</div><div /><div />
                </div>
                {rows.map((r) => {
                  const armed = armedTicker === r.ticker;
                  const busy = busyTicker === r.ticker;
                  return (
                    <div
                      key={r.ticker}
                      className="grid grid-cols-[72px_1fr_92px_70px_64px_56px_32px_32px] gap-2 px-3 py-1.5 items-center text-xs border-b border-border/50 last:border-b-0 hover:bg-muted/30"
                      data-testid={`expired-warrant-row-${r.ticker}`}
                    >
                      <button
                        type="button"
                        onClick={() => onStockClick && onStockClick(r.ticker)}
                        className="font-bold text-left text-primary hover:underline"
                        title={`Open ${r.ticker} in the Stock Modal`}
                        data-testid={`expired-warrant-open-${r.ticker}`}
                      >
                        {r.ticker}
                      </button>
                      <span className="text-muted-foreground truncate" title={r.name || ''}>{r.name || '—'}</span>
                      <span className="tabular-nums">{r.expiration_date}</span>
                      <span className="tabular-nums text-amber-600 dark:text-amber-400">{r.days_expired ?? '—'}</span>
                      <span className="tabular-nums">{r.price != null ? `$${Number(r.price).toFixed(2)}` : '—'}</span>
                      {r.cik ? (
                        <a
                          href={`https://www.sec.gov/cgi-bin/browse-edgar?action=getcompany&CIK=${r.cik}&type=&dateb=&owner=include&count=40`}
                          target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-0.5 text-blue-500 hover:underline"
                          title={`Issuer EDGAR filings (CIK ${r.cik})`}
                          data-testid={`expired-warrant-edgar-${r.ticker}`}
                        >
                          CIK <ExternalLink className="w-3 h-3" />
                        </a>
                      ) : <span className="text-muted-foreground">—</span>}
                      <button
                        type="button"
                        onClick={() => trash(r.ticker)}
                        disabled={busy}
                        className={`inline-flex items-center justify-center w-6 h-6 rounded transition-all disabled:opacity-40 ${
                          armed
                            ? 'bg-red-600 text-white ring-2 ring-red-300 animate-pulse'
                            : 'text-muted-foreground hover:text-red-500 hover:bg-red-500/10'
                        }`}
                        title={armed
                          ? `Click again to confirm: blocklist ${r.ticker} permanently and purge it everywhere`
                          : `Blocklist ${r.ticker} (bad-ticker database) + purge from all caches. Two clicks to confirm.`}
                        data-testid={`expired-warrant-trash-${r.ticker}`}
                      >
                        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                      </button>
                      <button
                        type="button"
                        onClick={() => dismiss(r.ticker)}
                        disabled={busy}
                        className="inline-flex items-center justify-center w-6 h-6 rounded text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-40"
                        title={`Drop ${r.ticker} from this list only — keeps it tracked (use if the expiration date is wrong)`}
                        data-testid={`expired-warrant-dismiss-${r.ticker}`}
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
